import { ReactNode, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useLocation,
  useSearchParams
} from "react-router-dom";

import { AWSCognitoUser } from "types/AWSCognito";
import { Auth } from "aws-amplify";
import { Spinner } from "@adfenix/alexandria";
import { jwtPayload } from "atoms/auth";
import { useAdfenixToken } from "hooks/useAdfenixToken";
import { useCognito } from "hooks/useCognito";
import { useNavigateByRole } from "hooks/useNavigateByRole";
import { useRecoilValue } from "recoil";
import {
  isAmplifyConfigExists,
  setAmplifyConfigToStorage,
  getAmplifyConfigFromStorage,
  getSsoReturnUrl,
  removeSsoReturnUrl
} from "api/ssoHelper";
import amplify from "lib/amplify/amplify";
import { getCognitoConfigById } from "api/services/authService";
import Sentry from "lib/sentry/init";

export const RefreshCredentialsHOC = ({
  children
}: {
  children: ReactNode;
}) => {
  const {
    usesAdfenixToken,
    fetchedAdfenixToken,
    isTokenExpired,
    isLoadingToken
  } = useAdfenixToken();
  const { persistCognitoPayload } = useCognito();
  useNavigateByRole();
  const [searchParams] = useSearchParams();

  const [fetchingCognitoJWT, setFetchingCognitoJWT] = useState(false);
  const isAuthed = useRecoilValue(jwtPayload);
  const [loggingInToCognito, setLoggingInToCognito] = useState(true);

  const checkedCognitoAndUsesAdfenixToken = !isAuthed && !loggingInToCognito;
  const loadingNonExpiredToken =
    usesAdfenixToken && !isTokenExpired && !fetchedAdfenixToken;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const refreshTokenIfUserExists = async () => {
      const search = new URLSearchParams(location.search);

      if (
        location.search.toLowerCase().includes("?idp=") &&
        !usesAdfenixToken
      ) {
        if (search.has("error_description") || search.has("error")) {
          Sentry.captureException("SSO error", {
            extra: {
              search: location.search
            }
          });
        }

        const cognitoConfig = await getCognitoConfigById(
          search?.get("idp")?.toString()
        );
        setAmplifyConfigToStorage(cognitoConfig);
        amplify(getAmplifyConfigFromStorage());
      }
      try {
        if (isAmplifyConfigExists()) {
          setFetchingCognitoJWT(true);
          const cognitoUser: AWSCognitoUser =
            await Auth.currentAuthenticatedUser({ bypassCache: true });

          if (cognitoUser) {
            const refreshToken = cognitoUser
              .getSignInUserSession()
              ?.getRefreshToken();

            if (refreshToken) {
              persistCognitoPayload(cognitoUser);

              cognitoUser.refreshSession(refreshToken, () => {
                setLoggingInToCognito(false);
                setFetchingCognitoJWT(false);
              });
            }
          }
        } else throw "aa user is not authenticated";
      } catch (e) {
        if (
          !location.pathname.includes("login") &&
          !usesAdfenixToken &&
          !isLoadingToken
        ) {
          if (location.pathname || location.search) {
            const returnUrl = encodeURIComponent(
              location.pathname + location.search
            );
            let extraParam = "";

            if (
              location.pathname.startsWith("/order/") ||
              location.pathname.startsWith("/v3/order/")
            ) {
              const guid = searchParams.get("guid");
              const programInstanceId = searchParams.get("programInstanceId");

              if (guid) {
                extraParam = `&propertyId=${guid}`;
              } else if (programInstanceId) {
                extraParam = `&programInstanceId=${programInstanceId}`;
              }
            }

            navigate(`/login?returnUrl=${returnUrl}${extraParam}`);
            return;
          } else navigate("/login");
        }
        setLoggingInToCognito(false);
      }

      const returnUrl = getSsoReturnUrl();

      if (returnUrl) {
        removeSsoReturnUrl();
        navigate(returnUrl);
      }
    };

    if (!isAuthed && !fetchingCognitoJWT) {
      refreshTokenIfUserExists();
    } else if (isAuthed) {
      setLoggingInToCognito(false);
    }
  }, [
    persistCognitoPayload,
    isAuthed,
    fetchingCognitoJWT,
    navigate,
    location,
    usesAdfenixToken,
    getCognitoConfigById
  ]);

  if (loggingInToCognito || loadingNonExpiredToken) {
    return (
      <div>
        <Spinner centered />
      </div>
    );
  }

  if (isTokenExpired && checkedCognitoAndUsesAdfenixToken) {
    return (
      <Routes>
        <Route element={<Navigate to={"/error/expired-token"} replace />} />
      </Routes>
    );
  }

  return <>{children}</>;
};
